import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

/** Duración de la animación cortina del modal (debe coincidir con el CSS en tablet) */
const MODAL_CURTAIN_DURATION_MS = 500;

export class ModalManager implements Plugin {
  private readonly content: HTMLElement | null;
  private readonly closeButtons: NodeListOf<HTMLElement>;
  private isOpen: boolean = false;
  private closeTimeout: ReturnType<typeof setTimeout> | null = null;

  private hasOtherOpenModalOverlays(): boolean {
    const overlays = document.querySelectorAll<HTMLElement>(
      ".com-modalB2C__overlay, .com-modalB2B__overlay",
    );

    return Array.from(overlays).some((overlay) => {
      if (overlay === this.element) return false;
      if (!overlay.classList.contains("is-open")) return false;
      return window.getComputedStyle(overlay).display !== "none";
    });
  }

  constructor(private readonly element: HTMLElement) {
    this.content = element.querySelector('[data-name="com-modalB2B-content"]');
    this.closeButtons = element.querySelectorAll(
      ".modal-close-btn, [data-modal-close]",
    );
  }

  init(): void {
    this.close();
    this.element.addEventListener("modal:open", () => this.open());
    this.element.addEventListener("modal:close", () => this.close());

    this.element.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (this.isOpen && target === this.element) {
        this.close();
      }
    });

    this.closeButtons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.close();
      });
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        if (this.element.style.display !== "none") {
          this.close();
        }
      }
    });
  }

  public open(): void {
    if (this.closeTimeout !== null) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    this.isOpen = true;
    this.element.style.display = "flex";
    document.body.style.overflow = "hidden";
    // Retrasar is-open un frame para que se pinte primero left: -360px y la transición se vea suave (efecto cortina)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.element.classList.add("is-open");
      });
    });
    this.element.dispatchEvent(
      new CustomEvent("modal:opened", { bubbles: true }),
    );
  }

  public close(): void {
    this.isOpen = false;
    this.element.classList.remove("is-open");
    this.element.dispatchEvent(
      new CustomEvent("modal:closed", { bubbles: true }),
    );
    // Esperar a que termine la animación de cierre (cortina hacia la izquierda) antes de ocultar el overlay
    this.closeTimeout = setTimeout(() => {
      this.element.style.display = "none";
      document.body.style.overflow = this.hasOtherOpenModalOverlays()
        ? "hidden"
        : "";
      this.closeTimeout = null;
    }, MODAL_CURTAIN_DURATION_MS);
  }
}

PluginRegistry.register("modal-manager", ModalManager);
